import moment from 'moment';
import { getBookingCalendarData, getDashboardPendingTasks } from '../services/api';
import {
  DASHBOARD_CALENDAR_CACHE_TTL_MS,
  DASHBOARD_PENDING_TASKS_TTL_MS,
  fetchWithDashboardCache,
  getDashboardCalendarCacheKey,
  getDashboardPendingTasksCacheKey,
  readDashboardCache,
} from './dashboardDataCache';
import { CALENDAR_GRID_DAY_COUNT, readStoredGridDayCount } from './dateUtils';
import { dashboardNeedsRefresh } from './dashboardRefreshCoordinator';

export function resolveDashboardCalendarRequest(
  displayView = 'ultra',
  currentDate = new Date(),
  gridDayCount = readStoredGridDayCount() || CALENDAR_GRID_DAY_COUNT,
) {
  const localDateStr = moment(currentDate).format('YYYY-MM-DD');
  let startMoment = moment(localDateStr, 'YYYY-MM-DD', true).startOf('day');
  let endMoment = startMoment.clone();

  if (displayView === 'ultra') {
    endMoment = startMoment.clone();
  } else if (displayView === 'calendar') {
    endMoment = startMoment.clone().add(Math.max(1, gridDayCount) - 1, 'days');
  } else if (displayView === 'month') {
    const monthStart = startMoment.clone().startOf('month');
    const monthEnd = startMoment.clone().endOf('month');
    startMoment = monthStart.clone().startOf('week');
    endMoment = monthEnd.clone().endOf('week');
  } else {
    endMoment = startMoment.clone().add(Math.max(1, gridDayCount) - 1, 'days');
  }

  const startDate = startMoment.format('YYYY-MM-DD');
  const endDate = endMoment.format('YYYY-MM-DD');
  const viewType = displayView === 'ultra' ? 'day' : 'week';

  return {
    startDate,
    endDate,
    viewType,
    cacheKey: getDashboardCalendarCacheKey(startDate, endDate, viewType),
  };
}

export function readCachedCalendarPayload(
  displayView = 'ultra',
  currentDate = new Date(),
  gridDayCount = readStoredGridDayCount() || CALENDAR_GRID_DAY_COUNT,
) {
  if (dashboardNeedsRefresh()) {
    return null;
  }
  const { cacheKey } = resolveDashboardCalendarRequest(displayView, currentDate, gridDayCount);
  return readDashboardCache(cacheKey);
}

let prefetchInflight = null;

/** Warm dashboard caches only when data is already fresh (avoids stale prefetch races). */
export function prefetchDashboardHomeData(options = {}) {
  const {
    displayView = 'ultra',
    currentDate = new Date(),
    gridDayCount = readStoredGridDayCount() || CALENDAR_GRID_DAY_COUNT,
    forceRefresh = false,
  } = options;

  if (!forceRefresh && dashboardNeedsRefresh()) {
    return Promise.resolve();
  }

  if (prefetchInflight && !forceRefresh) {
    return prefetchInflight;
  }

  const { startDate, endDate, viewType, cacheKey } = resolveDashboardCalendarRequest(
    displayView,
    currentDate,
    gridDayCount,
  );
  const todayStr = moment().format('YYYY-MM-DD');
  const tasksKey = getDashboardPendingTasksCacheKey(todayStr);

  if (!forceRefresh && readDashboardCache(cacheKey) && readDashboardCache(tasksKey)) {
    return Promise.resolve();
  }

  prefetchInflight = Promise.all([
    fetchWithDashboardCache(
      cacheKey,
      () => getBookingCalendarData(startDate, endDate, viewType, { refresh: forceRefresh }),
      { ttl: DASHBOARD_CALENDAR_CACHE_TTL_MS, forceRefresh },
    ),
    fetchWithDashboardCache(
      tasksKey,
      () => getDashboardPendingTasks(todayStr, { refresh: forceRefresh }),
      { ttl: DASHBOARD_PENDING_TASKS_TTL_MS, forceRefresh },
    ),
  ])
    .catch(() => {
      // Prefetch is best-effort; page load handles errors.
    })
    .finally(() => {
      prefetchInflight = null;
    });

  return prefetchInflight;
}

export function scheduleDashboardPrefetch(options = {}) {
  if (typeof window === 'undefined') return undefined;

  const run = () => {
    prefetchDashboardHomeData(options);
  };

  if (typeof window.requestIdleCallback === 'function') {
    const idleId = window.requestIdleCallback(run, { timeout: 1200 });
    return () => window.cancelIdleCallback(idleId);
  }

  const timerId = window.setTimeout(run, 0);
  return () => window.clearTimeout(timerId);
}
